import { useCallback, useEffect, useRef, useState } from 'react';
import { VOICE_MAX_SEC } from '../data/constants';

/**
 * Voice notes (rule N1).
 *
 * THE CRITICAL RULE: when the microphone is unavailable, this NEVER falls back
 * to a file picker. On iOS an `<input type="file" accept="audio/*">` opens the
 * CAMERA, which at a booth means the buyer thinks they are recording a note and
 * is actually filming the floor. The fallback is a typed/dictated note instead —
 * `unavailable` is how the caller knows to open that modal.
 *
 * MIME preference order matches the prototype: audio/mp4 (Safari), then
 * webm/opus, then plain webm.
 */

const MIME_ORDER = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_ORDER) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch (e) {
      /* some browsers throw rather than returning false */
    }
  }
  return '';
}

export default function useRecorder({ onDone, maxSec = VOICE_MAX_SEC } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [unavailable, setUnavailable] = useState(false);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const startedRef = useRef(0);

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recRef.current = null;
    setRecording(false);
    setSeconds(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch (e) {
        cleanup();
      }
    } else {
      cleanup();
    }
  }, [cleanup]);

  const start = useCallback(async () => {
    if (recording) {
      stop();
      return;
    }
    const mime = pickMime();
    if (mime === null || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setUnavailable(true);
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      // Denied, or no microphone. Either way: the typed-note fallback, never a
      // file picker.
      setUnavailable(true);
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      cleanup();
      setUnavailable(true);
      return;
    }

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const type = rec.mimeType || mime || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      const duration = Math.max(1, Math.round((Date.now() - startedRef.current) / 1000));
      cleanup();
      if (blob.size > 0 && onDone) onDone({ blob, duration, type });
    };

    recRef.current = rec;
    startedRef.current = Date.now();
    rec.start();
    setRecording(true);
    setSeconds(0);

    tickRef.current = setInterval(() => {
      const elapsed = Math.round((Date.now() - startedRef.current) / 1000);
      setSeconds(elapsed);
      if (elapsed >= maxSec) stop();
    }, 250);
  }, [recording, stop, cleanup, onDone, maxSec]);

  return {
    recording,
    seconds,
    /** True once the mic has proven unusable — open the typed-note modal. */
    unavailable,
    dismissUnavailable: () => setUnavailable(false),
    toggle: start,
    stop,
  };
}
